import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChoicesResponse, QuizDirection } from '../lib/types.js';
import { fetchChoices, fetchDictionary } from '../lib/api.js';
import { QuizCard } from './QuizCard.js';
import { MatchCelebration } from './MatchCelebration.js';
import { FamUpToast } from './FamUpToast.js';

type AnswerOutcome = { familiarity: number; familiarity_before: number };

type Props = {
  initialWords: string[];
  onAnswer: (word: string, correct: boolean) => Promise<AnswerOutcome>;
  onComplete: (stats: { mastered: number; redoes: number }) => void;
};

const DIRECTIONS: QuizDirection[] = ['en2zh', 'zh2en', 'audio', 'cloze'];
function pickDirection(): QuizDirection {
  return DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)]!;
}

// Match celebration label uses translation_zh now (uniformly available on
// every ChoicesResponse), so we no longer need a per-direction lookup.

// History entry: a previously committed answer the user can revisit by
// pressing ⬅️. Currently we only retain the last entry — enough for "let me
// re-read what I just answered".
type HistoryEntry = { question: ChoicesResponse; pickedIndex: number };

export function LearnPhase({ initialWords, onAnswer, onComplete }: Props): JSX.Element {
  const [queue, setQueue] = useState<string[]>(initialWords);
  const [current, setCurrent] = useState<ChoicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState<{ word: string; phonetic: string | null; zh: string } | null>(
    null,
  );
  const [famDelta, setFamDelta] = useState<1 | -1 | null>(null);
  const [previous, setPrevious] = useState<HistoryEntry | null>(null);
  const [viewingPrevious, setViewingPrevious] = useState(false);
  const masteredRef = useRef(0);
  const redoesRef = useRef(0);
  const totalRef = useRef(initialWords.length);

  const advance = useCallback(
    async (q: string[]) => {
      if (q.length === 0) {
        onComplete({ mastered: masteredRef.current, redoes: redoesRef.current });
        return;
      }
      setLoading(true);
      const word = q[0]!;
      try {
        const choices = await fetchChoices(word, pickDirection());
        setCurrent(choices);
      } catch {
        try {
          const entry = await fetchDictionary(word);
          setCurrent({
            word: entry.word,
            direction: 'en2zh',
            question: entry.word,
            phonetic: entry.phonetic,
            translation_zh: entry.translation_zh,
            options: [entry.translation_zh, '...', '...', '...'],
            correct_index: 0,
            audio_word: null,
          });
        } catch {
          setQueue(q.slice(1));
          return;
        }
      }
      setLoading(false);
    },
    [onComplete],
  );

  useEffect(() => {
    void advance(queue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResult = useCallback(
    async ({ correct, pickedIndex }: { correct: boolean; pickedIndex: number }) => {
      if (!current) return;
      const word = current.word;
      let outcome: AnswerOutcome;
      try {
        outcome = await onAnswer(word, correct);
      } catch {
        return;
      }

      // Save the just-answered question into history so ⬅️ on the next card
      // brings the user back here.
      setPrevious({ question: current, pickedIndex });
      setViewingPrevious(false);

      let nextQueue: string[];
      if (correct) {
        masteredRef.current += 1;
        nextQueue = queue.slice(1);
      } else {
        redoesRef.current += 1;
        nextQueue = [...queue.slice(1), word];
      }
      setQueue(nextQueue);

      const isMatch = outcome.familiarity_before === 3 && outcome.familiarity === 4;
      if (isMatch) {
        setMatch({
          word: current.word,
          phonetic: current.phonetic,
          zh: current.translation_zh,
        });
        return;
      }
      const delta =
        outcome.familiarity > outcome.familiarity_before
          ? 1
          : outcome.familiarity < outcome.familiarity_before
            ? -1
            : 0;
      if (delta !== 0) setFamDelta(delta as 1 | -1);
      void advance(nextQueue);
    },
    [current, queue, onAnswer, advance],
  );

  const handleMatchDone = useCallback(() => {
    setMatch(null);
    void advance(queue);
  }, [queue, advance]);

  // ⬅️ in live mode enters history; ➡️ ("回到当前") in history mode exits.
  const handleToggleHistory = useCallback(() => {
    if (!previous) return;
    setViewingPrevious((v) => !v);
  }, [previous]);

  if (loading || !current) {
    return <div className="text-secondary text-hint">载入中...</div>;
  }

  const displayed: ChoicesResponse = viewingPrevious && previous ? previous.question : current;
  const historyView = viewingPrevious && previous ? { pickedIndex: previous.pickedIndex } : null;
  const canGoBack = !viewingPrevious && previous != null;

  return (
    <>
      <QuizCard
        question={displayed}
        onResult={handleResult}
        resetKey={`${displayed.word}-${displayed.direction}-${viewingPrevious ? 'hist' : 'live'}`}
        progress={{ mastered: masteredRef.current, total: totalRef.current }}
        historyView={historyView}
        canGoBack={canGoBack}
        onBack={handleToggleHistory}
      />
      <MatchCelebration
        show={match !== null}
        word={match?.word ?? ''}
        phonetic={match?.phonetic ?? null}
        translationZh={match?.zh ?? ''}
        onDone={handleMatchDone}
      />
      <FamUpToast show={famDelta !== null} delta={famDelta ?? 1} onDone={() => setFamDelta(null)} />
    </>
  );
}
